import { http } from './httpClient';

// Authenticated file download for MVC (non-proxy) routes that stream a file
// (e.g. /Symbols/DownloadInstruments, /LoginInformation/DownloadTemplate).
// The shared `http` instance carries the bearer token + antiforgery header, so
// we reuse it with responseType:'blob' and trigger a browser download. The
// server's Content-Disposition filename is honoured when present, else a
// caller-supplied fallback is used.
export async function downloadFile(url: string, fallbackFileName: string): Promise<void> {
  const res = await http.get<Blob>(url, { responseType: 'blob' });
  const disposition = res.headers['content-disposition'] as string | undefined;
  const fileName = parseFileName(disposition) ?? fallbackFileName;

  const blobUrl = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(blobUrl);
}

// Authenticated file download for proxy/MVC routes that stream a file and accept
// query parameters (e.g. /api/app/clients-assets/extract-excel?referenceDate=…).
// `downloadFile` takes no params; this variant forwards them on the blob GET.
export async function downloadQuery(
  url: string,
  params: Record<string, unknown>,
  fallbackFileName: string,
): Promise<void> {
  const res = await http.get<Blob>(url, { params, responseType: 'blob' });
  const disposition = res.headers['content-disposition'] as string | undefined;
  const fileName = parseFileName(disposition) ?? fallbackFileName;

  const blobUrl = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(blobUrl);
}

function parseFileName(disposition: string | undefined): string | null {
  if (!disposition) return null;
  // RFC 5987 (filename*=UTF-8''…) first, then a plain filename="…".
  const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(disposition);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].replace(/^"|"$/g, ''));
    } catch {
      /* fall through */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  return plain?.[1] ?? null;
}

// Authenticated multipart upload for MVC routes (field name defaults to `file`).
// Overrides the JSON default Content-Type so axios sets the multipart boundary;
// the request interceptor still attaches the bearer + antiforgery headers.
export async function uploadFile<T>(url: string, file: File, fieldName = 'file'): Promise<T> {
  const form = new FormData();
  form.append(fieldName, file);
  const res = await http.post<T>(url, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
}
